/**
 * Atlas PM · 系统设置 · 任务模板管理 · 数据 hooks（TanStack Query）
 *
 * ⚠️ 失效范围用 `['task-templates']` 前缀，可同时刷新：
 *   · 本管理面板列表
 *   · P8.1「从模板新建」选择弹窗（其 key 同为 ['task-templates', 'list', …]）
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  taskTemplateAdminApi,
  type TaskTemplateListParams,
  type TaskTemplateUpdateInput,
} from './api';

export const taskTemplateAdminKeys = {
  /** 前缀：涵盖本面板与 P8.1 选择弹窗的所有模板查询 */
  all: ['task-templates'] as const,
  list: (params: TaskTemplateListParams) => ['task-templates', 'admin-list', params] as const,
};

export function useTaskTemplateList(params: TaskTemplateListParams) {
  return useQuery({
    queryKey: taskTemplateAdminKeys.list(params),
    queryFn: () => taskTemplateAdminApi.list(params),
    placeholderData: keepPreviousData,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: taskTemplateAdminKeys.all });
}

export function useUpdateTaskTemplate(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: TaskTemplateUpdateInput) => taskTemplateAdminApi.update(id, input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteTaskTemplate() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => taskTemplateAdminApi.remove(id),
    onSuccess: () => invalidate(),
  });
}
